import React from "react";
import { useLocation, useParams } from "react-router-dom";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import PageLoader from "../../../../common/ui/PageLoader";
import ScrollableWrapper from "../common/ScrollableWrapper";
import ClientBadge from "../common/ClientBadge";
import PrescriptionLinkButton from "../common/PrescriptionLinkButton";
import ServiceContentView from "../common/ServiceContentView";
import HistoryNotReadyState from "./HistoryNotReadyState";
import useHistoryScope from "./useHistoryScope";
import { useServiceHistoryDetail } from "./useServiceHistory";
import {
  SERVICE_HISTORY_LABELS,
  SERVICE_HISTORY_UNIT_TYPE,
} from "./serviceHistoryMapper";

/**
 * `…/programming/:serviceId` — one prescribed service, full page.
 *
 * The design (node 16605:100602) is `programming/ServiceViewModal` without the
 * dialog, so the body is literally that modal's: both render
 * `common/ServiceContentView`. This component is only the page chrome — the
 * card and the client badge. The first version copied the modal's markup
 * instead, and the two had already drifted on image height, benefit order,
 * whether benefits were clickable and the type scale.
 *
 * The record comes from `GET /history/services/{id}`, which returns the
 * report's `ServiceContent` for this client plus the client's name, so the
 * page — badge included — renders cold from a pasted URL with one request.
 */
const ServiceHistoryDetailView = () => {
  const theme: Theme = useTheme();
  const { serviceId } = useParams();
  const { kind, basePath } = useHistoryScope();
  const location = useLocation();
  const { service, clientName, prescriptionId, loading, notFound, error } =
    useServiceHistoryDetail(serviceId);

  if (loading) return <PageLoader />;

  if (error) {
    return <HistoryNotReadyState title="Couldn't load this service" text={error} />;
  }

  if (notFound || !service) {
    return (
      <HistoryNotReadyState
        title="Service not found"
        text="This service is no longer available. Go back to Programming to see the current list."
      />
    );
  }

  return (
    <div style={styles.container}>
      {/* No title or back button: the breadcrumb names the page and carries
          the link back to the listing (`useBackNavigation`). */}
      <div style={styles.contentWrapper}>
        <ScrollableWrapper>
          <div style={styles.contentContainer(theme)}>
            <ServiceContentView
              service={service}
              labels={SERVICE_HISTORY_LABELS}
              unitType={SERVICE_HISTORY_UNIT_TYPE}
              // The page is roughly twice the modal's width, so the attributes
              // fit three columns rather than two.
              attributeColumns={3}
              // Only here, not in the programming modal that shares this body.
              // `location.search` keeps the workspace's `?id=` client.
              titleAside={
                prescriptionId ? (
                  <PrescriptionLinkButton
                    to={`${basePath}/prescription/${prescriptionId}${location.search}`}
                  />
                ) : undefined
              }
              headerRight={
                // Only the cross-client page needs to name the client — the
                // workspace shells already show them in the top bar.
                kind === "company" ? (
                  <ClientBadge name={clientName} />
                ) : undefined
              }
            />
          </div>
        </ScrollableWrapper>
      </div>
    </div>
  );
};

export default ServiceHistoryDetailView;

const styles = {
  container: {
    width: "100%",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as "column",
    boxSizing: "border-box" as "border-box",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden" as "hidden",
    marginRight: "-10px",
  },
  contentContainer: (theme: Theme) => ({
    padding: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  }),
};
